/**
 * Pool lifecycle APIs (M2 backend) — pause / unpause / retire / restore
 * plus the dashboard endpoints that drive the workbench Lifecycle tab.
 *
 * Endpoint map (mirrors ``rest_handlers.py``):
 *
 *   POST   /networks/{id}/pools/{poolId}/pause     → { pool, changed, fromStatus, toStatus }
 *   POST   /networks/{id}/pools/{poolId}/unpause   → { pool, changed, ... }
 *   POST   /networks/{id}/pools/{poolId}/retire    → { pool, changed, ... }
 *   POST   /networks/{id}/pools/{poolId}/restore   → { pool, changed, ... }
 *   GET    /networks/{id}/pools/lifecycle          → { pools, counts: {active, notified, paused, retired, archived} }
 *   GET    /tenants/{tenantId}/pools/lifecycle     → { pools, counts }
 *   GET    /tenants/{tenantId}/settings            → { settings }
 *   PUT    /tenants/{tenantId}/settings            → { settings }
 *
 * The five canonical lifecycle bucket keys are exported here so the
 * frontend never re-stringifies them. They line up with
 * ``models.pool_lifecycle.LIFECYCLE_BUCKETS`` on the backend.
 */
import { getRestApiBearerToken } from './apiToken';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://api.stepflow.dev';

export const LIFECYCLE_BUCKET_ACTIVE = 'active';
export const LIFECYCLE_BUCKET_NOTIFIED = 'notified';
export const LIFECYCLE_BUCKET_PAUSED = 'paused';
export const LIFECYCLE_BUCKET_RETIRED = 'retired';
export const LIFECYCLE_BUCKET_ARCHIVED = 'archived';

export const LIFECYCLE_BUCKETS = Object.freeze([
  LIFECYCLE_BUCKET_ACTIVE,
  LIFECYCLE_BUCKET_NOTIFIED,
  LIFECYCLE_BUCKET_PAUSED,
  LIFECYCLE_BUCKET_RETIRED,
  LIFECYCLE_BUCKET_ARCHIVED,
]);

export const RETIRE_REASON_MANUAL_ADMIN = 'MANUAL_ADMIN';
export const RETIRE_REASON_MANUAL_SUPERVISOR = 'MANUAL_SUPERVISOR';

function encodePath(s) {
  return encodeURIComponent(String(s || '').trim());
}

async function authedFetch(path, init = {}) {
  const token = getRestApiBearerToken();
  if (!token) throw new Error('Not authenticated');
  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init.headers || {}),
    },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = data.error || data.message || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/**
 * Fetch the lifecycle dashboard for one network.
 *
 * Response shape::
 *
 *     {
 *       networkId: string,
 *       pools: PoolApi[],          // every visible pool, lifecycleStatus attached
 *       counts: {                  // bucket -> int (always all five keys, zeros included)
 *         active, notified, paused, retired, archived,
 *       },
 *       includesAdminOnly: boolean // RETIRED + ARCHIVED only when caller is workspace admin
 *     }
 *
 * @param {string} networkId
 * @param {{ bucket?: 'active'|'notified'|'paused'|'retired'|'archived' }} [opts]
 *   Optional bucket filter. Server returns the full ``pools`` list when omitted.
 */
export async function getNetworkPoolsLifecycle(networkId, opts = {}) {
  const qs = new URLSearchParams();
  if (opts.bucket) qs.set('bucket', String(opts.bucket).toLowerCase());
  const q = qs.toString();
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/lifecycle${q ? `?${q}` : ''}`,
  );
}

/**
 * Tenant-wide lifecycle dashboard. Workspace OWNER/ADMIN only — the
 * backend rejects callers without that role even if they have CREATOR
 * on individual pools, since this view is meant for the Overwatch
 * console.
 *
 * @param {string} tenantId
 * @param {{ bucket?: string, limit?: number }} [opts]
 */
export async function getTenantPoolsLifecycle(tenantId, opts = {}) {
  const qs = new URLSearchParams();
  if (opts.bucket) qs.set('bucket', String(opts.bucket).toLowerCase());
  if (opts.limit) qs.set('limit', String(opts.limit));
  const q = qs.toString();
  return authedFetch(
    `/tenants/${encodePath(tenantId)}/pools/lifecycle${q ? `?${q}` : ''}`,
  );
}

// ---------------------------------------------------------------------------
// Per-pool transitions
// ---------------------------------------------------------------------------
//
// The four endpoints below all accept the same minimal body — a
// free-text ``reason`` recorded in the audit trail — and return the
// post-update pool plus a ``changed`` flag that's ``false`` for an
// idempotent re-click. The wire-protocol shape mirrors
// :func:`rest_handlers._perform_pool_transition` 1:1.

/**
 * @param {string} networkId
 * @param {string} poolId
 * @param {{ reason?: string }} [body]
 */
export async function pausePool(networkId, poolId, body = {}) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/pause`,
    { method: 'POST', body: JSON.stringify(body || {}) },
  );
}

/**
 * @param {string} networkId
 * @param {string} poolId
 * @param {{ reason?: string }} [body]
 */
export async function unpausePool(networkId, poolId, body = {}) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/unpause`,
    { method: 'POST', body: JSON.stringify(body || {}) },
  );
}

/**
 * Retire a pool — either immediately or scheduled for ``inDays``
 * days from now (which moves it to ``NOTIFIED`` first, then the
 * nightly sweeper auto-retires when the deadline elapses).
 *
 * Use :func:`buildRetireRequestBody` to validate the form payload
 * before calling this; that helper is unit-tested separately so the
 * UI dialog and the request shape never drift.
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {{ mode: 'immediate'|'schedule', inDays?: number, reason?: string }} body
 */
export async function retirePool(networkId, poolId, body) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/retire`,
    { method: 'POST', body: JSON.stringify(body || {}) },
  );
}

/**
 * Reactivate a RETIRED or ARCHIVED pool. Workspace admin only.
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {{ reason?: string }} [body]
 */
export async function restorePool(networkId, poolId, body = {}) {
  return authedFetch(
    `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}/restore`,
    { method: 'POST', body: JSON.stringify(body || {}) },
  );
}

/**
 * Read the audit timeline for one pool — every ``PoolLifecycleEvent``
 * row written by the transition service. Drives the Lifecycle audit
 * drawer.
 *
 * Response shape::
 *
 *     {
 *       networkId, poolId,
 *       newestFirst: boolean,
 *       events: Array<{
 *         occurredAt, actor, actorKind: 'USER'|'SYSTEM',
 *         toLifecycleStatus, fromLifecycleStatus?, reason?,
 *         sequence?, metadata?: object,
 *       }>,
 *       count, limit,
 *     }
 *
 * Authorization: workspace admin sees every pool's audit; pool members
 * see only the pools they're part of; everyone else gets 403.
 *
 * @param {string} networkId
 * @param {string} poolId
 * @param {{ limit?: number, newestFirst?: boolean }} [opts]
 */
export async function listPoolLifecycleEvents(networkId, poolId, opts = {}) {
  const params = new URLSearchParams();
  if (opts && Number.isFinite(opts.limit)) {
    params.set('limit', String(Math.max(1, Math.min(200, Math.trunc(opts.limit)))));
  }
  if (opts && opts.newestFirst === false) {
    params.set('newestFirst', 'false');
  }
  const qs = params.toString();
  const path
    = `/networks/${encodePath(networkId)}/pools/${encodePath(poolId)}`
    + `/lifecycle-events${qs ? `?${qs}` : ''}`;
  return authedFetch(path);
}

// ---------------------------------------------------------------------------
// Workspace settings (retention policy)
// ---------------------------------------------------------------------------

/**
 * @param {string} tenantId
 */
export async function getWorkspaceSettings(tenantId) {
  return authedFetch(`/tenants/${encodePath(tenantId)}/settings`);
}

/**
 * Update tenant retention policy. Server-side validates that
 * ``inactiveNotifyDays < inactiveRetireDays`` and that every numeric
 * field is non-negative — stale form state still produces a 400 with
 * a useful ``message`` rather than silently saving an invalid policy.
 *
 * @param {string} tenantId
 * @param {{
 *   inactiveNotifyDays?: number,
 *   inactiveRetireDays?: number,
 *   archiveAfterDays?: number,
 *   sweeperEnabled?: boolean,
 *   builderWorkspaceSoftCapGb?: number,
 *   builderCodebuildTimeoutMinutes?: number,
 *   builderMaxConcurrentRuns?: number,
 *   builderMonthlyBedrockUsdCap?: number,
 * }} body
 */
export async function putWorkspaceSettings(tenantId, body) {
  return authedFetch(`/tenants/${encodePath(tenantId)}/settings`, {
    method: 'PUT',
    body: JSON.stringify(body || {}),
  });
}

// ---------------------------------------------------------------------------
// Pure helpers (extracted so Vitest can pin the wire shape)
// ---------------------------------------------------------------------------

/**
 * Build the request body for :func:`retirePool` from the
 * ``RetirePoolDialog`` form state. Two valid shapes:
 *
 *   * ``{ mode: 'immediate', reason }``           → Retire now.
 *   * ``{ mode: 'schedule', inDays: N, reason }`` → Move to NOTIFIED with
 *                                                  ``RetireAt = now+N days``.
 *
 * Throws on unknown modes / non-positive day counts so a wired-up form
 * with bad state can't silently send a malformed body.
 *
 * @param {{ mode: string, inDays?: number|string, reason?: string }} input
 */
export function buildRetireRequestBody(input) {
  if (!input || typeof input !== 'object') {
    throw new Error('buildRetireRequestBody: input is required');
  }
  const mode = String(input.mode || '').trim().toLowerCase();
  const reason = (input.reason || '').toString().trim();

  if (mode === 'immediate') {
    const body = { mode: 'immediate' };
    if (reason) body.reason = reason;
    return body;
  }

  if (mode === 'schedule') {
    const raw = input.inDays;
    const n = typeof raw === 'string' ? Number(raw) : raw;
    if (!Number.isFinite(n) || n <= 0 || Math.floor(n) !== n) {
      throw new Error(
        'buildRetireRequestBody: schedule mode requires a positive integer "inDays"',
      );
    }
    const body = { mode: 'schedule', inDays: n };
    if (reason) body.reason = reason;
    return body;
  }

  const shown = input.mode === undefined || input.mode === null || input.mode === ''
    ? '<empty>'
    : JSON.stringify(input.mode);
  throw new Error(`buildRetireRequestBody: unsupported mode ${shown}`);
}
